/**
 * CustomizationUI - Left-side menu for item/color selection
 */
export class CustomizationUI {
  constructor(container, onColorSelect) {
    this.container = container || document.body;
    this.onColorSelect = onColorSelect; // Callback: (itemName, colorConfig) => {}
    this.menuElement = null;
    this.currentItems = [];
  }

  /**
   * Create the UI menu
   */
  create() {
    this.menuElement = document.createElement('div');
    this.menuElement.id = 'customization-menu';
    this.menuElement.innerHTML = `
      <div class="customization-header">
        <h3>🎨 Customize</h3>
        <button id="toggle-customization" class="toggle-btn">◀</button>
      </div>
      <div class="customization-content" id="customization-items">
        <p class="no-items">No customizable items available</p>
      </div>
    `;
    
    this.container.appendChild(this.menuElement);
    this.setupEventListeners();
  }

  /**
   * Update menu with available items and colors
   */
  updateItems(items) {
    this.currentItems = items;
    const contentElement = document.getElementById('customization-items');
    
    if (!items || items.length === 0) {
      contentElement.innerHTML = '<p class="no-items">No customizable items in this view</p>';
      return;
    }
    
    contentElement.innerHTML = items.map(item => `
      <div class="customization-item" data-item="${item.name}">
        <h4 class="item-label">${item.label}</h4>
        <div class="color-swatches">
          ${item.colors.map(color => `
            <button 
              class="color-swatch" 
              data-item="${item.name}"
              data-color="${color.name}"
              style="background-color: ${color.hex};"
              title="${color.name}"
            >
              <span class="color-name">${color.name}</span>
            </button>
          `).join('')}
          <button 
            class="color-swatch reset-swatch" 
            data-item="${item.name}"
            data-color="reset"
            title="Reset to original"
          >
            ✕
          </button>
        </div>
      </div>
    `).join('');
    
    // Add click listeners to color swatches
    contentElement.querySelectorAll('.color-swatch').forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        const itemName = e.currentTarget.dataset.item;
        const colorName = e.currentTarget.dataset.color;
        
        if (colorName === 'reset') {
          this.handleReset(itemName);
        } else {
          this.handleColorSelect(itemName, colorName);
        }
      });
    });
  }

  /**
   * Handle color selection
   */
  handleColorSelect(itemName, colorName) {
    const item = this.currentItems.find(i => i.name === itemName);
    const color = item.colors.find(c => c.name === colorName);
    
    if (this.onColorSelect) {
      this.onColorSelect(itemName, color);
    }
    
    // Highlight selected swatch
    this.highlightSwatch(itemName, colorName);
  }

  /**
   * Handle reset to original
   */
  handleReset(itemName) {
    if (this.onColorSelect) {
      this.onColorSelect(itemName, null); // null = remove overlay
    }
    
    // Remove highlight
    this.highlightSwatch(itemName, null);
  }

  /**
   * Highlight the active color swatch
   */
  highlightSwatch(itemName, colorName) {
    const itemElement = document.querySelector(`[data-item="${itemName}"]`);
    if (!itemElement) return;
    
    // Remove all active states for this item
    itemElement.querySelectorAll('.color-swatch').forEach(swatch => {
      swatch.classList.remove('active');
    });
    
    // Add active state to selected swatch
    if (colorName) {
      const activeSwatch = itemElement.querySelector(`[data-color="${colorName}"]`);
      if (activeSwatch) {
        activeSwatch.classList.add('active');
      }
    }
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    const toggleBtn = document.getElementById('toggle-customization');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        this.menuElement.classList.toggle('collapsed');
        toggleBtn.textContent = this.menuElement.classList.contains('collapsed') ? '▶' : '◀';
      });
    }
  }

  /**
   * Show the menu
   */
  show() {
    if (this.menuElement) {
      this.menuElement.style.display = 'block';
    }
  }

  /**
   * Hide the menu
   */
  hide() {
    if (this.menuElement) {
      this.menuElement.style.display = 'none';
    }
  }

  /**
   * Refresh the UI with current items
   */
  refresh() {
    // Re-render the items if we have any
    if (this.currentItems && this.currentItems.length > 0) {
      this.updateItems(this.currentItems);
    }
  }

  /**
   * Clean up
   */
  dispose() {
    if (this.menuElement) {
      this.menuElement.remove();
    }
  }
}


